// ============================================================
// Admin Console — /intel/console
// One-admin login + 3 tabs (Twins · Tracking · Search & Sources)
// ============================================================
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { MentionsDiff } from "../components/MentionsDiff";
import { ClientsTab } from "../components/ClientsTab";
import { BlogTab } from "../components/blog/admin/BlogTab";
import { PromptTab } from "../components/PromptTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "np_admin_token";

const ax = axios.create({ baseURL: API, withCredentials: true });

// Attach Authorization: Bearer header from localStorage as a fallback
// when cross-site cookies are blocked (mobile Chrome, Safari ITP, etc.).
ax.interceptors.request.use((config) => {
  const t = typeof window !== "undefined" ? window.localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

const fmtErr = (d) => {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (d.msg) return d.msg;
  return String(d);
};

// --- LOGIN -----------------------------------------------------------------
function Login({ onLogin }) {
  const [username, setUsername] = useState("master@nettpilot.com");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await ax.post("/admin/login", { username, password });
      // Persist token for Bearer fallback (cookie still set too, belt + braces)
      if (data?.token && typeof window !== "undefined") {
        window.localStorage.setItem(TOKEN_KEY, data.token);
      }
      onLogin();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="intel-root min-h-screen flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="intel-panel max-w-md w-full p-8"
        style={{ "--panel-accent": "#ff7f50", borderLeftColor: "#ff7f50" }}
      >
        <div className="font-display tracking-[0.22em] text-[11px] uppercase text-nettred">
          // PRIVATE_CONSOLE
        </div>
        <h1 className="mt-4 font-display font-bold text-3xl text-coolwhite uppercase">Authenticate</h1>
        <p className="mt-3 font-term text-[13px] text-dim">
          Single-admin console. JWT in HttpOnly cookie · 12h session.
        </p>

        <label className="block mt-8">
          <span className="font-term text-[11px] tracking-[0.2em] uppercase text-dim">Username</span>
          <input
            data-testid="admin-login-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="mt-2 w-full bg-surface border border-edge px-3 py-2.5 font-term text-coolwhite outline-none focus:border-nettred"
          />
        </label>

        <label className="block mt-4">
          <span className="font-term text-[11px] tracking-[0.2em] uppercase text-dim">Password</span>
          <input
            data-testid="admin-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="mt-2 w-full bg-surface border border-edge px-3 py-2.5 font-term text-coolwhite outline-none focus:border-nettred"
          />
        </label>

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={submitting}
          className="mt-7 w-full bg-nettred text-space font-display tracking-[0.22em] text-[12px] uppercase py-3 hover:bg-amber hover:text-space transition-colors disabled:opacity-50"
        >
          {submitting ? "…" : "Authenticate →"}
        </button>

        <a
          href="/intel"
          className="mt-5 inline-block font-display tracking-[0.18em] text-[11px] uppercase text-dim hover:text-coolwhite"
        >
          ← back to intel
        </a>
      </form>
    </div>
  );
}

// --- TWINS TAB -------------------------------------------------------------
function TwinsTab() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ node: "", tag: "", state: "planned", href: "", order: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await ax.get("/admin/twins");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!draft.node || !draft.tag) return;
    setBusy(true);
    try {
      await ax.post("/admin/twins", { ...draft, order: Number(draft.order) || 0 });
      setDraft({ node: "", tag: "", state: "planned", href: "", order: 0 });
      await load();
      toast.success("Twin added.");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const save = async (it) => {
    try {
      await ax.put(`/admin/twins/${it.id}`, it);
      await load();
      toast.success("Saved.");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this twin entry?")) return;
    try {
      await ax.delete(`/admin/twins/${id}`);
      await load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-coolwhite uppercase">Twin Network</h2>
      <p className="mt-1 font-term text-[13px] text-dim">
        Edit the public list shown on /intel under SECTION_06.
      </p>

      <form onSubmit={add} className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-2">
        <input data-testid="twin-new-node" className={inp} placeholder="nickolass.base44.app"
               value={draft.node} onChange={e=>setDraft({...draft, node:e.target.value})} />
        <input data-testid="twin-new-tag" className={`${inp} md:col-span-4`} placeholder="BASE44 INSTANCE"
               value={draft.tag} onChange={e=>setDraft({...draft, tag:e.target.value})} />
        <select data-testid="twin-new-state" className={`${inp} md:col-span-2`}
                value={draft.state} onChange={e=>setDraft({...draft, state:e.target.value})}>
          <option value="planned">planned</option>
          <option value="live">live</option>
          <option value="self">self (this node)</option>
          <option value="stealth">stealth</option>
        </select>
        <input data-testid="twin-new-href" className={`${inp} md:col-span-3`} placeholder="https://…"
               value={draft.href} onChange={e=>setDraft({...draft, href:e.target.value})} />
        <button data-testid="twin-new-submit" disabled={busy}
                className="md:col-span-1 bg-nettred text-space font-display tracking-[0.18em] text-[11px] uppercase py-2 hover:bg-amber hover:text-space transition-colors disabled:opacity-50">
          + add
        </button>
      </form>

      <div className="mt-8 space-y-2">
        {items.length === 0 && <div className="font-term text-dim">// no twins yet — public /intel will show the static default fallback</div>}
        {items.map((it) => (
          <TwinRow key={it.id} it={it} onSave={save} onDelete={remove} />
        ))}
      </div>
    </div>
  );
}

const inp = "bg-surface border border-edge px-3 py-2 font-term text-[13.5px] text-coolwhite outline-none focus:border-nettred";

function TwinRow({ it, onSave, onDelete }) {
  const [draft, setDraft] = useState(it);
  useEffect(() => setDraft(it), [it]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(it);
  return (
    <div className="intel-panel p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center"
         style={{"--panel-accent":"#10b981", borderLeftColor:"#10b981"}}>
      <input className={inp} value={draft.node} onChange={e=>setDraft({...draft, node:e.target.value})} />
      <input className={`${inp} md:col-span-4`} value={draft.tag} onChange={e=>setDraft({...draft, tag:e.target.value})} />
      <select className={`${inp} md:col-span-2`} value={draft.state} onChange={e=>setDraft({...draft, state:e.target.value})}>
        <option value="planned">planned</option><option value="live">live</option>
        <option value="self">self</option><option value="stealth">stealth</option>
      </select>
      <input className={`${inp} md:col-span-3`} placeholder="(no link)" value={draft.href || ""} onChange={e=>setDraft({...draft, href:e.target.value})} />
      <div className="md:col-span-1 flex gap-2 justify-end">
        {dirty && (
          <button onClick={() => onSave(draft)}
            className="text-[10px] font-display tracking-[0.18em] uppercase text-amber hover:text-coolwhite px-2 py-1">save</button>
        )}
        <button onClick={() => onDelete(it.id)}
          className="text-[10px] font-display tracking-[0.18em] uppercase text-dim hover:text-nettred px-2 py-1">del</button>
      </div>
    </div>
  );
}

// --- TRACKING TAB ----------------------------------------------------------
function TrackingTab() {
  const [html, setHtml] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ax.get("/admin/settings").then(({ data }) => {
      setHtml(data.tracking_html || "");
      setLoaded(true);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await ax.put("/admin/settings", { tracking_html: html });
      toast.success("Tracking HTML saved. New visits will load it.");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-coolwhite uppercase">Tracking & Scripts</h2>
      <p className="mt-1 font-term text-[13px] text-dim">
        Pasted as raw HTML into the document &lt;head&gt; on every public page.
        Use for GA4, Plausible, Cookiebot, Cloudflare Analytics, etc.
      </p>
      <textarea
        data-testid="tracking-textarea"
        value={html}
        onChange={(e) => setHtml(e.target.value)}
        rows={14}
        placeholder={`<!-- Example -->\n<script defer data-domain="nickolass.com" src="https://plausible.io/js/script.js"></script>`}
        className="mt-5 w-full bg-surface border border-edge px-3 py-3 font-term text-[12.5px] text-coolwhite outline-none focus:border-nettred resize-y"
      />
      <div className="mt-4 flex items-center gap-3">
        <button data-testid="tracking-save"
          onClick={save} disabled={!loaded || saving}
          className="bg-nettred text-space font-display tracking-[0.22em] text-[12px] uppercase px-5 py-2.5 hover:bg-amber hover:text-space transition-colors disabled:opacity-50">
          {saving ? "…" : "Save"}
        </button>
        <span className="font-term text-[11px] text-dim">// stored once · served to all visitors via /api/tracking</span>
      </div>

      <div className="mt-10 intel-panel p-4 font-term text-[12px] text-dim"
           style={{"--panel-accent":"#e6c84a", borderLeftColor:"#e6c84a"}}>
        <strong className="text-amber">heads up:</strong> what you paste here is injected verbatim into every visitor's
        browser. Only paste scripts from sources you trust. GDPR best-practice: include a consent banner (Cookiebot,
        Klaro, or similar) <em>before</em> the analytics script if it sets non-essential cookies.
      </div>
    </div>
  );
}

// --- SEARCH & SOURCES TAB --------------------------------------------------
function SearchTab() {
  const [data, setData] = useState(null);
  const [domains, setDomains] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [booking, setBooking] = useState("");

  const load = async () => {
    const { data } = await ax.get("/admin/settings");
    setData(data);
    setBooking(data.booking_embed_url || "");
    setDomains((data.verified_domains || []).join("\n"));
    setEnabled(Boolean(data.search_enabled));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await ax.put("/admin/settings", {
        search_enabled: enabled,
        verified_domains: domains.split("\n").map(s => s.trim()).filter(Boolean),
        booking_embed_url: booking.trim(),
      });
      toast.success("Search settings saved.");
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  if (!data) return <div className="font-term text-dim">// loading…</div>;

  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-coolwhite uppercase">Grounded Search</h2>
      <p className="mt-1 font-term text-[13px] text-dim">
        Perplexity Sonar (<code className="text-coolwhite">{data.perplexity_model}</code>) runs before Claude on every chat.
        Only the verified domains below are queried. Sources surface as inline citations.
      </p>

      <div className="mt-6 intel-panel p-4"
           style={{"--panel-accent": data.perplexity_key_set ? "#10b981" : "#ff7f50",
                   borderLeftColor: data.perplexity_key_set ? "#10b981" : "#ff7f50"}}>
        <div className="font-display tracking-[0.2em] text-[11px] uppercase"
             style={{color: data.perplexity_key_set ? "#10b981" : "#ff7f50"}}>
          // PERPLEXITY_API_KEY · {data.perplexity_key_set ? "configured" : "missing"}
        </div>
        {!data.perplexity_key_set && (
          <p className="mt-2 font-term text-[12.5px] text-dim">
            Add <code className="text-coolwhite">PERPLEXITY_API_KEY=pplx-...</code> to <code className="text-coolwhite">/app/backend/.env</code>,
            then restart the backend. The toggle below has no effect until the key is set.
          </p>
        )}
      </div>

      <label className="mt-6 flex items-center gap-3 cursor-pointer select-none">
        <input data-testid="search-toggle" type="checkbox" checked={enabled}
               onChange={(e) => setEnabled(e.target.checked)}
               className="h-4 w-4 accent-nettred" />
        <span className="font-display tracking-[0.18em] text-[12px] uppercase text-coolwhite">
          Search grounding {enabled ? "ON" : "OFF"}
        </span>
      </label>

      <div className="mt-6">
        <div className="font-display tracking-[0.18em] text-[11px] uppercase text-dim mb-2">
          // VERIFIED_DOMAINS (one per line, max 10 used per query)
        </div>
        <textarea
          data-testid="search-domains"
          value={domains}
          onChange={(e) => setDomains(e.target.value)}
          rows={10}
          className="w-full bg-surface border border-edge px-3 py-3 font-term text-[13px] text-coolwhite outline-none focus:border-nettred resize-y"
        />
      </div>

      <div className="mt-6">
        <div className="font-display tracking-[0.18em] text-[11px] uppercase text-dim mb-2">
          // BOOKING_EMBED_URL (Calendly / cal.com / TidyCal / Google-avtaleside)
        </div>
        <input data-testid="booking-url" value={booking} onChange={(e) => setBooking(e.target.value)}
          placeholder="https://calendly.com/ditt-navn/30min"
          className="w-full bg-surface border border-edge px-3 py-2.5 font-term text-[13px] text-coolwhite outline-none focus:border-nettred" />
        <p className="mt-2 font-term text-[11.5px] text-dim">
          Vises innebygd i chatten når tvillingen merker at besøkende vil snakke med deg. Tomt = bookingkort med lenke til nettpilot.no/book-avtale og telefon.
        </p>
      </div>

      <button data-testid="search-save" onClick={save}
        className="mt-5 bg-nettred text-space font-display tracking-[0.22em] text-[12px] uppercase px-5 py-2.5 hover:bg-amber hover:text-space transition-colors">
        Save
      </button>
    </div>
  );
}

// --- AI MENTIONS TAB -------------------------------------------------------
function MentionsTab() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [openWeb, setOpenWeb] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [config, setConfig] = useState(null);
  const [runningWeekly, setRunningWeekly] = useState(false);
  const [clientId, setClientId] = useState("");

  const load = async () => {
    try {
      const [list, cfg] = await Promise.all([
        ax.get("/admin/mentions"),
        ax.get("/admin/mentions/config"),
      ]);
      setItems(list.data);
      setConfig(cfg.data);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []);

  const snapshot = async () => {
    setBusy(true);
    try {
      await ax.post("/admin/mentions/snapshot", { query: query.trim() || null, open_web: openWeb });
      toast.success("Snapshot tatt.");
      setQuery("");
      await load();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const runWeekly = async () => {
    if (!window.confirm(`Kjør den ukentlige batchen nå (${config?.queries?.length || 0} spørringer)?`)) return;
    setRunningWeekly(true);
    try {
      const { data } = await ax.post("/admin/mentions/run-weekly");
      toast.success(`Batch fullført: ${data.ran} snapshots lagt til.`);
      await load();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally {
      setRunningWeekly(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Slette dette øyeblikksbildet?")) return;
    try {
      await ax.delete(`/admin/mentions/${id}`);
      await load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  const fmtCountdown = (s) => {
    if (!s || s < 0) return "—";
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${d}d ${h}t ${m}m`;
  };
  const dayLabel = ["mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lørdag", "søndag"];

  return (
    <div data-testid="mentions-tab">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <h2 className="font-display font-bold text-2xl text-coolwhite uppercase">AI Mention Tracker</h2>
        <div className="font-term text-[11px] text-dim">// {items.length} snapshots</div>
      </div>
      <p className="mt-1 font-term text-[13px] text-dim max-w-3xl">
        Spør Perplexity hva AI-er sier om Nettpilot / 3LA / Nickolass — på det åpne webet, eller bare innenfor verifiserte
        domener. Hvert øyeblikksbilde lagres permanent, så du kan se utvikling over tid og dokumentere 3LA-effekten for kunder.
      </p>

      {/* Weekly auto-snapshot status */}
      {config && (
        <div className="intel-panel p-5 mt-6" style={{"--panel-accent": config.enabled ? "#10b981" : "#a3acb9", borderLeftColor: config.enabled ? "#10b981" : "#a3acb9"}}>
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="font-display tracking-[0.2em] text-[11px] uppercase" style={{color: config.enabled ? "#10b981" : "#a3acb9"}}>
              // WEEKLY_AUTO_SNAPSHOT · {config.enabled ? "ACTIVE" : "DISABLED"}
            </div>
            <button
              data-testid="mentions-run-weekly"
              onClick={runWeekly}
              disabled={runningWeekly}
              className="font-display tracking-[0.18em] text-[11px] uppercase px-3 py-1.5 border border-edge text-coolwhite hover:bg-nettred hover:border-nettred hover:text-space transition-colors disabled:opacity-50"
            >
              {runningWeekly ? "Kjører…" : "Kjør batch nå →"}
            </button>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 font-term text-[12.5px]">
            <div>
              <div className="text-dim text-[11px] uppercase tracking-[0.15em]">Tidsplan</div>
              <div className="text-coolwhite mt-1">{dayLabel[config.day]} kl. {String(config.hour).padStart(2, "0")}:00 ({config.tz})</div>
            </div>
            <div>
              <div className="text-dim text-[11px] uppercase tracking-[0.15em]">Neste kjøring</div>
              <div className="text-coolwhite mt-1">om {fmtCountdown(config.next_run_in_seconds)}</div>
            </div>
            <div>
              <div className="text-dim text-[11px] uppercase tracking-[0.15em]">Spørringer per batch</div>
              <div className="text-coolwhite mt-1">{config.queries.length}</div>
            </div>
          </div>
          <details className="mt-3">
            <summary className="font-display tracking-[0.18em] text-[11px] uppercase text-amber cursor-pointer hover:text-coolwhite">
              ▸ vis spørringene
            </summary>
            <ul className="mt-3 space-y-1 font-term text-[12.5px] text-coolwhite/85 pl-4">
              {config.queries.map((q, i) => (
                <li key={i}><span className="text-dim">{i + 1}.</span> {q}</li>
              ))}
            </ul>
          </details>
        </div>
      )}

      <ClientPicker value={clientId} onChange={setClientId} />
      <MentionsDiff ax={ax} refreshKey={items.length} clientId={clientId} />

      {/* Manual snapshot form */}
      <div className="intel-panel p-5 mt-4" style={{"--panel-accent":"#38bdf8", borderLeftColor:"#38bdf8"}}>
        <div className="font-display tracking-[0.2em] text-[11px] uppercase text-l2 mb-3">
          // MANUAL_SNAPSHOT
        </div>
        <textarea
          data-testid="mentions-query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          rows={3}
          placeholder={"Tom = standardspørringen: «What is the Three-Layer Approach, who is Nickolass Jensen, what is Nettpilot known for?»"}
          className="w-full bg-surface border border-edge px-3 py-2.5 font-term text-[13px] text-coolwhite outline-none focus:border-nettred resize-y"
        />
        <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              data-testid="mentions-openweb"
              type="checkbox"
              checked={openWeb}
              onChange={(e) => setOpenWeb(e.target.checked)}
              className="h-4 w-4 accent-nettred"
            />
            <span className="font-display tracking-[0.18em] text-[11px] uppercase text-coolwhite/90">
              {openWeb ? "ÅPENT WEB (anbefalt)" : "BARE VERIFISERTE DOMENER"}
            </span>
          </label>
          <button
            data-testid="mentions-snapshot-btn"
            onClick={snapshot}
            disabled={busy}
            className="bg-nettred text-space font-display tracking-[0.22em] text-[12px] uppercase px-5 py-2.5 hover:bg-amber hover:text-space transition-colors disabled:opacity-50"
          >
            {busy ? "Spør Perplexity…" : "Ta øyeblikksbilde →"}
          </button>
        </div>
      </div>

      {/* Snapshots list */}
      <div className="mt-8 space-y-3">
        {!loaded && <div className="font-term text-dim">// laster…</div>}
        {loaded && items.length === 0 && (
          <div className="font-term text-dim">// ingen snapshots ennå — trykk «Ta øyeblikksbilde» for å starte loggen.</div>
        )}
        {items.map((it) => <MentionCard key={it.id} it={it} onDelete={remove} />)}
      </div>
    </div>
  );
}

function ClientPicker({ value, onChange }) {
  const [clients, setClients] = useState([]);
  useEffect(() => { ax.get("/admin/clients").then(({ data }) => setClients(data)).catch(() => {}); }, []);
  if (clients.length === 0) return null;
  return (
    <div className="mt-6 flex items-center gap-3 flex-wrap">
      <span className="font-display tracking-[0.2em] text-[11px] uppercase text-dim">// rapport for</span>
      <select data-testid="mentions-client-picker" value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-surface border border-edge px-3 py-1.5 font-term text-[13px] text-coolwhite outline-none focus:border-nettred">
        <option value="">Nettpilot (egen)</option>
        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </div>
  );
}

function MentionCard({ it, onDelete }) {
  const [open, setOpen] = useState(false);
  const accent = it.self_citation_count > 0 ? "#10b981" : "#e6c84a";
  return (
    <div className="intel-panel p-5" style={{"--panel-accent": accent, borderLeftColor: accent}}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="font-term text-[12px] text-dim">
          {new Date(it.timestamp).toLocaleString()} · <span className="text-coolwhite">{it.open_web ? "OPEN WEB" : "VERIFIED ONLY"}</span> · {it.model}
          {it.auto && <span className="ml-2 px-1.5 py-0.5 border border-l2 text-l2 font-display tracking-[0.15em] text-[10px] uppercase">auto</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-hud text-amber text-[12px] tabular-nums">{it.citation_count} cites</span>
          {it.self_citation_count > 0 && (
            <span className="px-2 py-0.5 border border-l3 text-l3 font-display tracking-[0.18em] text-[10px] uppercase">
              {it.self_citation_count} self
            </span>
          )}
          <button
            onClick={() => onDelete(it.id)}
            className="ml-2 text-[10px] font-display tracking-[0.18em] uppercase text-dim hover:text-nettred px-2 py-1"
          >del</button>
        </div>
      </div>

      <div className="mt-3 font-display tracking-[0.18em] text-[11px] uppercase text-coolwhite">
        // QUERY
      </div>
      <p className="mt-1 font-term text-[13px] text-coolwhite/90">{it.query}</p>

      <button
        onClick={() => setOpen(!open)}
        className="mt-4 font-display tracking-[0.18em] text-[11px] uppercase text-amber hover:text-coolwhite"
      >
        {open ? "▾ skjul detaljer" : "▸ vis sammendrag + kilder"}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <div>
            <div className="font-display tracking-[0.18em] text-[11px] uppercase text-dim">// AI SUMMARY</div>
            <p className="mt-2 font-term text-[13.5px] leading-[1.75] text-coolwhite/90 whitespace-pre-wrap">{it.summary || "(tomt)"}</p>
          </div>
          {it.citations?.length > 0 && (
            <div>
              <div className="font-display tracking-[0.18em] text-[11px] uppercase text-dim">// CITATIONS ({it.citations.length})</div>
              <ul className="mt-2 space-y-1 font-term text-[12.5px]">
                {it.citations.map((c, i) => (
                  <li key={i} className="flex items-baseline gap-2">
                    <span className="text-dim tabular-nums">[{i + 1}]</span>
                    <a href={c.url} target="_blank" rel="noreferrer"
                       className="text-coolwhite hover:text-nettred transition-colors break-all">
                      {c.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- STATS TAB -------------------------------------------------------------
function StatsTab() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await ax.get("/admin/stats");
        if (!cancelled) setS(data);
      } catch (e) {
        if (!cancelled) setErr(fmtErr(e.response?.data?.detail) || e.message);
      }
    };
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (err) return <div className="font-term text-nettred">// {err}</div>;
  if (!s) return <div className="font-term text-dim">// loading stats…</div>;

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h2 className="font-display font-bold text-2xl text-coolwhite uppercase">Live Stats</h2>
        <div className="font-term text-[11px] text-dim">
          // refreshed {new Date(s.as_of).toLocaleString()}
        </div>
      </div>

      {/* KPI row */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI value={s.sessions.last_7d} label="Sessions · 7d" accent="#10b981" sub={`${s.sessions.all_time} all-time`} />
        <KPI value={s.leads.last_7d}    label="Leads · 7d"    accent="#ff7f50" sub={`${s.leads.all_time} all-time`} />
        <KPI value={s.messages.last_7d} label="Messages · 7d" accent="#38bdf8" sub={`${s.messages.all_time} all-time`} />
        <KPI value={s.sessions.active_now} label="Active in RAM" accent="#e6c84a" sub="LlmChat instances" />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top citation hosts */}
        <div className="intel-panel p-5" style={{"--panel-accent":"#10b981", borderLeftColor:"#10b981"}}>
          <div className="font-display tracking-[0.2em] text-[11px] uppercase text-l3">
            // top_citation_hosts · 30d
          </div>
          {s.top_citation_hosts_30d.length === 0 ? (
            <p className="mt-3 font-term text-dim">// none yet — needs Perplexity grounding enabled + activity</p>
          ) : (
            <ul className="mt-4 space-y-2 font-term text-[13.5px]">
              {s.top_citation_hosts_30d.map((h) => <BarRow key={h.host} label={h.host} n={h.n}
                max={Math.max(...s.top_citation_hosts_30d.map(x=>x.n))} color="#10b981" />)}
            </ul>
          )}
        </div>

        {/* Top user keywords */}
        <div className="intel-panel p-5" style={{"--panel-accent":"#38bdf8", borderLeftColor:"#38bdf8"}}>
          <div className="font-display tracking-[0.2em] text-[11px] uppercase text-l2">
            // top_user_keywords · 30d
          </div>
          {s.top_user_keywords_30d.length === 0 ? (
            <p className="mt-3 font-term text-dim">// no chats yet in window</p>
          ) : (
            <div className="mt-4 flex flex-wrap gap-2">
              {s.top_user_keywords_30d.map((w) => (
                <span key={w.word} className="font-term text-[12px] px-2 py-1 border border-edge text-coolwhite"
                      style={{fontSize: `${Math.min(18, 11 + Math.log2(w.n + 1)*2)}px`}}>
                  {w.word} <span className="text-dim">·{w.n}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Language split */}
        <div className="intel-panel p-5" style={{"--panel-accent":"#fb923c", borderLeftColor:"#fb923c"}}>
          <div className="font-display tracking-[0.2em] text-[11px] uppercase text-l1">
            // language_split · user_messages
          </div>
          <ul className="mt-4 space-y-2 font-term text-[13.5px]">
            {s.languages.length === 0
              ? <li className="text-dim">// no data</li>
              : s.languages.map((l) => <BarRow key={l.lang} label={l.lang.toUpperCase()} n={l.n}
                  max={Math.max(...s.languages.map(x=>x.n))} color="#fb923c" />)}
          </ul>
        </div>

        {/* Recent leads */}
        <div className="intel-panel p-5" style={{"--panel-accent":"#ff7f50", borderLeftColor:"#ff7f50"}}>
          <div className="font-display tracking-[0.2em] text-[11px] uppercase text-nettred">
            // recent_leads · last 5
          </div>
          {s.leads.recent.length === 0 ? (
            <p className="mt-3 font-term text-dim">// none yet</p>
          ) : (
            <ul className="mt-4 space-y-3 font-term text-[12.5px]">
              {s.leads.recent.map((l) => (
                <li key={l.id} className="border-b border-edge pb-3 last:border-0">
                  <div className="text-coolwhite">{l.name} <span className="text-dim">· {l.email}</span></div>
                  <div className="text-dim text-[11px] mt-0.5">
                    {new Date(l.timestamp).toLocaleString()} · {l.lang?.toUpperCase()} · {l.source}
                  </div>
                  {l.ai_summary && (
                    <div data-testid="lead-ai-summary" className="text-l3 mt-1">↳ {l.ai_summary}</div>
                  )}
                  <div className="text-coolwhite/85 mt-1 line-clamp-2">{l.message}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ value, label, sub, accent }) {
  return (
    <div className="intel-panel p-4" style={{"--panel-accent": accent, borderLeftColor: accent}}>
      <div className="font-hud font-bold text-amber text-3xl md:text-4xl tracking-wider">{value}</div>
      <div className="mt-2 font-display tracking-[0.2em] text-[10.5px] uppercase text-coolwhite/90">// {label}</div>
      {sub && <div className="mt-1 font-term text-[10.5px] text-dim">{sub}</div>}
    </div>
  );
}

function BarRow({ label, n, max, color }) {
  const pct = max > 0 ? (n / max) * 100 : 0;
  return (
    <li>
      <div className="flex items-baseline justify-between text-coolwhite">
        <span className="truncate mr-2">{label}</span>
        <span className="font-hud text-amber text-[12.5px] tabular-nums">{n}</span>
      </div>
      <div className="h-[3px] mt-1 bg-edge">
        <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </li>
  );
}

// --- SHELL -----------------------------------------------------------------
export default function AdminConsole() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("stats");

  useEffect(() => {
    ax.get("/admin/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const logout = async () => {
    await ax.post("/admin/logout");
    if (typeof window !== "undefined") window.localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="intel-root min-h-screen flex items-center justify-center">
        <div className="font-term text-dim">// checking session…</div>
      </div>
    );
  }
  if (!authed) return (
    <>
      <Login onLogin={() => setAuthed(true)} />
      <Toaster richColors position="top-center" theme="dark" />
    </>
  );

  return (
    <div className="intel-root min-h-screen">
      <header className="border-b border-edge bg-space/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 min-h-14 py-2 flex flex-wrap items-center justify-between gap-y-2">
          <div className="font-display tracking-[0.22em] text-[12px] uppercase text-coolwhite">
            <span className="h-2 w-2 inline-block bg-nettred rounded-full mr-2 animate-pulse-dot align-middle" />
            ADMIN_CONSOLE
          </div>
          <nav className="flex flex-wrap items-center gap-1">
            {["stats", "blog", "instruks", "mentions", "clients", "twins", "tracking", "search"].map((k) => (
              <button
                key={k}
                data-testid={`tab-${k}`}
                onClick={() => setTab(k)}
                className={`font-display tracking-[0.18em] text-[11px] uppercase px-3 py-1.5 border ${
                  tab === k ? "border-nettred text-coolwhite" : "border-transparent text-dim hover:text-coolwhite"
                }`}
              >// {k}</button>
            ))}
            <a href="/intel" className="ml-3 font-display tracking-[0.18em] text-[11px] uppercase text-dim hover:text-coolwhite">↗ /intel</a>
            <button data-testid="admin-logout" onClick={logout}
              className="ml-3 font-display tracking-[0.18em] text-[11px] uppercase text-dim hover:text-nettred">
              logout
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-6 md:px-10 py-10">
        {tab === "stats" && <StatsTab />}
        {tab === "mentions" && <MentionsTab />}
        {tab === "clients" && <ClientsTab ax={ax} fmtErr={fmtErr} />}
        {tab === "blog" && <BlogTab ax={ax} />}
        {tab === "instruks" && <PromptTab ax={ax} />}
        {tab === "twins" && <TwinsTab />}
        {tab === "tracking" && <TrackingTab />}
        {tab === "search" && <SearchTab />}
      </main>

      <Toaster richColors position="top-center" theme="dark" />
    </div>
  );
}
